import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import AppTextField from "../../../../../../components/common/forms/AppTextField";
import { useEJOESRApprovalContext } from "../../Context/EJOESRApprovalContext";
import { useGetServiceRequestTypeQuery } from "../../../../../../hooks/useServiceRequestType";
import { EJOSRManagementInitialValues, EJOSRSchema } from "./utils/schema";
import { EJOSR, EJOSRFormFields } from "./utils/types";
import { useGetPMSCodesByModel } from "../../../../../../hooks/usePMSCode";
import { Box, Typography } from "@mui/material";
import { useSnackBar } from "../../../../../../components/ToastProvider";
import AppDialog from "../../../../../../components/common/AppDialog";
import AppSelect from "../../../../../../components/common/forms/AppSelect";
import EJOSRConfirmDeleteDialog from "./EJOSRConfirmDeleteDialog";
import ImageUploader from "../../../../../../components/common/ImageUploader";
import ImagePreviewer from "../../../../../../components/common/ImagePreviewer";
import { appColors } from "../../../../../../theme";

interface EJOSRProp {
  equipmentModel: string | undefined;
}

const EJOSRManagementForm: React.FC<EJOSRProp> = ({ equipmentModel }) => {
  const { showSnackBar } = useSnackBar();
  const {
    openDialog,
    setOpenDialog,
    serviceRequestsArray,
    setServiceRequestsArray,
    currentSRSelected,
    setCurrentSRSelected,
    srActionType,
    setIsEditing,
  } = useEJOESRApprovalContext();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EJOSRFormFields>({
    defaultValues: EJOSRManagementInitialValues,
    mode: "onChange",
    resolver: yupResolver(EJOSRSchema),
  });

  const currentSRTypeSelected: string = watch("serviceRequest");
  const [isPmServiceSelected, setIsPmServiceSelected] = useState(false);
  const [images, setImages] = useState<(File | string)[]>([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const serviceRequests = useGetServiceRequestTypeQuery();
  const pmsCodeData = useGetPMSCodesByModel(String(equipmentModel));

  const serviceRequestOptions: any[] = useMemo(() => {
    if (serviceRequests?.data === undefined) {
      return [];
    }

    const serviceRequestOptionsArray = serviceRequests.data
      .map((e) => {
        //* If there are no pm code found
        if (pmsCodeData.data?.length === 0) {
          //* Filter the Service Request Type PM Service to prevent PM Code select from appearing
          if (e.requestName === "PM Service") {
            return null;
          } else {
            return {
              id: e.id,
              label: e.requestName,
              value: e.id,
            };
          }
        } else {
          //* If there's a pm code found, just return the data as usual
          return {
            id: e.id,
            label: e.requestName,
            value: e.id,
          };
        }
      })
      .filter((option) => option !== null)
      .sort((a, b) => {
        // Default to empty string if A OR B is null or undefined
        const labelA = a?.label ?? "";
        const labelB = b?.label ?? "";

        return labelA.localeCompare(labelB);
      });

    const pmServiceIndex = serviceRequestOptionsArray.findIndex(
      (option) => option?.label === "PM Service"
    );

    if (pmServiceIndex !== -1) {
      // Remove the "PM Service" element from its current position
      const [pmService] = serviceRequestOptionsArray.splice(pmServiceIndex, 1);
      // Add "PM Service" to the end of the array
      serviceRequestOptionsArray.push(pmService);
    }

    return serviceRequestOptionsArray;
  }, [pmsCodeData.data]);

  const pmsCodeOptions: any[] =
    pmsCodeData?.data === undefined
      ? []
      : pmsCodeData.data.map((e) => {
          return {
            id: e.id,
            label: e.pmCode,
            value: e.id,
          };
        });

  const handleClose = () => {
    setValue("remarks", "");
    setValue("serviceRequest", "");
    setCurrentSRSelected({
      id: "",
      serviceRequest: "",
      serviceRequestTypeId: "",
      remarks: "",
      images: [],
    });
    setOpenDialog(false);
  };

  const onSubmit = (formData: EJOSRFormFields) => {
    const number = serviceRequestsArray.length + 1;
    const serviceRequested = serviceRequestOptions.find(
      (service) => formData.serviceRequest == service.id
    );

    let pmCodeFound = pmsCodeOptions.find((pm) => formData.pmCodeId == pm.id);

    const addToServiceRequestsArray = () => {
      const newSr: EJOSR = {
        id: number.toString(),
        serviceRequest: serviceRequested.label,
        serviceRequestTypeId: serviceRequested.id,
        remarks: !formData.remarks ? "" : formData.remarks,
        pmCodeId: !pmCodeFound ? "" : pmCodeFound.id,
        pmCode: !pmCodeFound ? "" : pmCodeFound.label,
        images: images,
      };
      if (!formData.remarks && !newSr.pmCodeId && !newSr.pmCode) {
        showSnackBar("Please fill out all the fields", "error");
      } else {
        setServiceRequestsArray((prev: EJOSR[]) => {
          return [...prev, newSr];
        });

        setIsEditing(true);
        handleClose();
      }
    };

    const updateServiceRequestArray = (index: number, newValue: EJOSR) => {
      if (!newValue.remarks && !newValue.pmCode && !newValue.pmCodeId) {
        showSnackBar("Please fill out all the fields", "error");
      } else {
        const newArray: EJOSR[] = [...serviceRequestsArray];

        newValue.serviceRequestNo = newArray[index]?.serviceRequestNo;
        newArray[index] = newValue;

        setServiceRequestsArray(newArray);
        setIsEditing(true);
        setCurrentSRSelected({
          id: "",
          serviceRequest: "",
          serviceRequestTypeId: "",
          remarks: "",
          images: [],
        });
        handleClose();
      }
    };

    if (srActionType == "new") {
      addToServiceRequestsArray();
    }

    if (srActionType == "update") {
      const newSr: EJOSR = {
        id: currentSRSelected.id,
        serviceRequestNo: currentSRSelected.serviceRequestNo,
        serviceRequest: serviceRequested.label,
        serviceRequestTypeId: serviceRequested.id,
        remarks: !formData.remarks ? "" : formData.remarks,
        pmCodeId: !pmCodeFound ? "" : pmCodeFound.id,
        pmCode: !pmCodeFound ? "" : pmCodeFound.label,
        images: images,
      };

      serviceRequestsArray.forEach((sr, index) => {
        if (sr.id == newSr.id) {
          updateServiceRequestArray(index, newSr);
        }
      });
    }
  };

  //* Change the remarks text field into PM Code select if PM Service is selected
  useEffect(() => {
    const srSelected = serviceRequestOptions.find(
      (sr) => sr.id == currentSRTypeSelected
    );

    if (srSelected) {
      // For changing ui, from textfield to select if pm service is selected
      if (srSelected.label.toLowerCase().trim() == "pm service") {
        setIsPmServiceSelected(true);
      } else {
        setIsPmServiceSelected(false);
        //* If not remove the value of pmCode and pmCodeId to avoid passing to request body
        setValue("pmCode", "");
        setValue("pmCodeId", "");
        //* Clear the textfield if you change service request type
        if (currentSRSelected.remarks != watch("remarks")) {
          setValue("remarks", "");
        }
      }
    }
  }, [currentSRTypeSelected]);

  useEffect(() => {
    const images = watch("images");

    setImages(images || []);
  }, [watch("images")]);

  useEffect(() => {
    if (!currentSRSelected) {
      console.log("Show the error");
      return;
    } else {
      setValue("serviceRequest", currentSRSelected.serviceRequestTypeId);
      setValue("serviceRequestTypeId", currentSRSelected.serviceRequestTypeId);
      setValue("pmCodeId", currentSRSelected.pmCodeId);
      setValue("pmCode", currentSRSelected.pmCodeId);
      setValue("remarks", currentSRSelected.remarks);
      setValue("images", currentSRSelected.images);
    }
  }, [currentSRSelected]);

  return (
    <AppDialog
      open={openDialog}
      onDialogClose={handleClose}
      title={"Service Request"}
      maxWidth="sm"
      sx={{ textAlign: "center" }}
    >
      <Stack
        component="form"
        height="100%"
        bgcolor="white"
        py={1}
        borderRadius={1}
      >
        <Box
          height="100%"
          overflow="auto"
          bgcolor="white"
          gap={2}
          sx={{ paddingTop: ".50em" }}
        >
          <Controller
            control={control}
            name="serviceRequest"
            render={({ field }) => (
              <AppSelect
                label="Service Request Type"
                labelAlignment="right"
                labelWidth="30%"
                fieldWidth="70%"
                fontWeight={400}
                placeholder="Select here"
                isOptionsNotSorted={true}
                error={!!errors.serviceRequest}
                helperText={errors.serviceRequest?.message}
                options={serviceRequestOptions}
                {...register("serviceRequest")}
                {...field}
              />
            )}
          />
          <Box sx={{ marginTop: "1em" }}>
            {isPmServiceSelected === false ? (
              <AppTextField
                label="Remarks"
                fontWeight={400}
                labelAlignment="right"
                labelWidth="30%"
                fieldWidth="70%"
                error={!!errors.remarks}
                helperText={errors.remarks?.message}
                {...register("remarks")}
              />
            ) : (
              <Controller
                control={control}
                name="pmCodeId"
                render={({ field }) => (
                  <AppSelect
                    label="PM Code"
                    labelAlignment="right"
                    labelWidth="30%"
                    fieldWidth="70%"
                    fontWeight={400}
                    placeholder="Select here"
                    error={!!errors.pmCode}
                    helperText={errors.pmCode?.message}
                    options={pmsCodeOptions}
                    {...register("pmCode")}
                    {...field}
                  />
                )}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "start", marginTop: "1em", gap: 1 }}>
            <Typography
              sx={{
                padding: 1,
                width: "30%",
              }}
              color={appColors.formText}
              component="label"
              fontSize={15}
              width={130}
              align={"right"}
              whiteSpace="nowrap"
            >
              Images
            </Typography>
            <Box sx={{ flex: 1 }}>
              <ImageUploader
                images={images}
                onChange={setImages}
                multiple
                maxCount={3}
                onImageClick={(i) => {
                  setViewerIndex(i);
                  setViewerOpen(true);
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: "100%", display: "flex" }}>
          <Button
            onClick={handleSubmit(onSubmit)}
            sx={{ width: 150, marginLeft: "auto", marginTop: "1.5em" }}
          >
            Save
          </Button>
        </Box>
        <EJOSRConfirmDeleteDialog />
      </Stack>
      <ImagePreviewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        images={images}
        initialIndex={viewerOpen ? viewerIndex : -1}
      />
    </AppDialog>
  );
};

export default EJOSRManagementForm;
